import { useLayoutEffect, useState } from 'react';
import type { Area, Point } from 'react-easy-crop';
import Cropper from 'react-easy-crop';

import classNames from 'classnames';

import Close from '@uikit/icons/Close';
import ZoomMinus from '@uikit/icons/ZoomMinus';
import ZoomPlus from '@uikit/icons/ZoomPlus';
import type { ModalOption } from '@uikit/index';
import { Button, Modal, ModalClose, ModalContent } from '@uikit/index';

type CropModalProps = ModalOption & {
  url?: string;
  onCrop?: (croppedArea: Area, croppedAreaPixels: Area) => void;
  isLoading?: boolean;
  className?: string;
  overlayClassName?: string;
  cropSize?: {
    width: number;
    height: number;
  };
  cropShape?: 'rect' | 'round';
  aspect?: number;
  isShowZoom?: boolean;
};

interface CropModalPreviewProps {
  zoom: number;
  url?: string;
  croppedArea: Area;
}

const CropModalPreview = ({ url, croppedArea }: CropModalPreviewProps) => {
  const scale = 100 / croppedArea.width || 0;
  const transform = {
    x: `${-croppedArea?.x * scale}%`,
    y: `${-croppedArea.y * scale}%`,
    scale,
    width: 'calc(100% + 0.5px)',
    height: 'auto',
  };
  const imageStyle = {
    transform: `translate3d(${transform.x}, ${transform.y}, 0) scale3d(${transform.scale},${transform.scale},1)`,
    width: transform.width,
    height: transform.height,
  };

  return (
    <img
      src={url}
      style={imageStyle}
      className="absolute h-auto top-0 left-0 origin-top-left"
      alt="Crop preview"
    />
  );
};

const ZeroPoint = { x: 0, y: 0 };

const CropModal = ({
  url,
  onCrop,
  isLoading,
  cropSize,
  cropShape = 'round',
  aspect = 1,
  isShowZoom = true,
  className,
  overlayClassName,
  ...props
}: CropModalProps) => {
  const [crop, setCrop] = useState<Point>(ZeroPoint);
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState<Area>();
  const [cropAreaPixels, setCropAreaPixels] = useState<Area>();

  const changeZoom = (type: 'plus' | 'minus') => {
    if (type === 'minus') {
      if (zoom > 1.05) {
        return setZoom(zoom - 0.05);
      }
    }

    if (type === 'plus') {
      if (zoom < 3) {
        return setZoom(zoom + 0.05);
      }

      return setZoom(3);
    }

    return setZoom(1);
  };

  const handleSaveButtonClick = () => {
    if (cropArea && cropAreaPixels && onCrop) {
      onCrop(cropArea, cropAreaPixels);
    }
  };

  const handleCropAreaChange = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCropArea(croppedArea);
    setCropAreaPixels(croppedAreaPixels);
  };

  useLayoutEffect(() => {
    setCrop(ZeroPoint);
    setZoom(1);
    setCropArea(undefined);
    setCropAreaPixels(undefined);
  }, [url]);

  return (
    <Modal {...props} isAnimationEnabled={false}>
      <ModalContent
        className={classNames(
          'w-full border-0 max-w-[400px] overflow-hidden flex flex-col px-0 items-center gap-6',
          className,
        )}
        data-qa="modal-window"
        overlayClassName={overlayClassName}
      >
        <ModalClose className="absolute right-4 text-secondary top-4 h-8 w-8 flex items-center justify-center">
          <Close className="icon" />
        </ModalClose>

        <div
          className={classNames({
            'w-24 h-24 rounded-full overflow-hidden shadow-dropdown relative':
              cropShape === 'round',
            'w-full max-w-48 overflow-hidden relative': cropShape === 'rect',
          })}
          style={{
            aspectRatio: cropShape === 'rect' ? aspect : undefined,
          }}
        >
          {cropArea && <CropModalPreview url={url} zoom={zoom} croppedArea={cropArea} />}
        </div>

        <div className="relative self-stretch h-52">
          <Cropper
            image={url}
            crop={crop}
            zoom={zoom}
            maxZoom={3}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={false}
            cropSize={cropSize}
            onCropChange={setCrop}
            onCropAreaChange={handleCropAreaChange}
            onZoomChange={setZoom}
          />
        </div>

        {isShowZoom && (
          <div className="flex justify-center gap-2">
            <button onClick={() => changeZoom('plus')}>
              <ZoomPlus className="icon-xl text-neutral-300" />
            </button>
            <button onClick={() => changeZoom('minus')}>
              <ZoomMinus className="icon-xl text-neutral-300" />
            </button>
          </div>
        )}

        <Button
          variant="primary"
          onClick={handleSaveButtonClick}
          disabled={isLoading}
          data-qa="profile-modal-avatar-save-button"
        >
          {isLoading ? 'Uploading...' : 'Save'}
        </Button>
      </ModalContent>
    </Modal>
  );
};

export default CropModal;
